import React, { FC, useState } from 'react'
import dayjs from 'dayjs'
import Spinner from '../../../components/common/loader/Loader'
import classNames from 'classnames'
import ActionsBox from './components/actions-box/ActionsBox'
import AudienceBox from './components/audience-box/AudienceBox'

import { FeedbackBox } from './components/feedback-box/FeedbackBox'
import {
  UserInsightsBox,
  fullWidthLimitOfSurveys
} from './components/user-insights-box/UserInsightsBox'
import { CustomMetricBox } from './components/custom-metric-box/CustomMetricBox'

import './DashboardSummary.less'
import { CustomMetricModal } from './components/custom-metric-box/CustomMetricModal'
import { useDashboardSummaryLazyQuery } from './DashboardSummary.codegen'
import {
  DateFilterType,
  useDateFilterContext,
  WithDateFilterContext
} from '../../../components/common/dateFilter'
import { predefinedDateRanges } from '../../../components/utils/predefinedDateRanges'
import DashboardsFilter, {
  DashboardsFiltersEnum
} from './components/dashboards-filter/DashboardsFilter'

interface IProps {
  dashboardId: number
  customMetricEnabled: boolean
}
export enum pageFormationEnum {
  STANDARD = 'STANDARD',
  SURVEYWIDE = 'SURVEYWIDE'
}

const DashboardSummaryComponent: FC<IProps> = ({
  dashboardId,
  customMetricEnabled
}) => {
  const [filters] = useDateFilterContext()
  const [showModal, setShowModal] = useState(false)

  const [refetch, { data }] = useDashboardSummaryLazyQuery({
    variables: {
      dashboardId: dashboardId,
      filters: {
        from: filters.from.toISOString(),
        to: filters.to.toISOString()
      }
    }
  })

  React.useEffect(() => {
    refetch()
  }, [filters])

  if (!data) {
    return <Spinner />
  }

  const dashboard = data?.admin?.dashboard

  const userInsights = dashboard?.userInsights || []
  const surveys = dashboard?.surveys || []
  const currentAudienceCount = dashboard?.currentAudienceCount || 0
  const commsPenetration = dashboard?.commsPenetration || 0
  const resourcesPenetration = dashboard?.resourcesPenetration || 0
  const commentsCount = dashboard?.commentedTopics.length || 0
  const ratedCurrentEnrolments = dashboard?.ratedCurrentEnrolments || []
  const actionsEffectiveness = dashboard?.actionsEffectiveness || []

  const totalActionsCount = actionsEffectiveness.reduce(
    (a, b) => a + (b['processedActionsCount'] || 0),
    0
  )

  const formation = () => {
    return surveys.length > fullWidthLimitOfSurveys
      ? pageFormationEnum.SURVEYWIDE
      : pageFormationEnum.STANDARD
  }

  return (
    <div
      className={classNames(
        'DataSummaryGrid',
        `DataSummaryGrid--${formation()}`
      )}
    >
      <DashboardsFilter activeFilters={[DashboardsFiltersEnum.DATE_FILTER]} />
      <div className="DataSummaryGrid__items">
        <AudienceBox
          dashboardId={dashboardId}
          isEmpty={!currentAudienceCount}
          totalNumber={currentAudienceCount}
          commsPercent={commsPenetration}
          resourcePercent={Math.round(resourcesPenetration)}
        />
        {dashboard && customMetricEnabled && (
          <>
            <CustomMetricBox
              from={filters.from.toISOString()}
              to={filters.to.toISOString()}
              editable={!!dashboard.isEditor}
              chartData={dashboard.customMetricEntriesFiltered || []}
              target={dashboard?.metric_target_value}
              metricName={dashboard?.metric_name}
              showModal={() => setShowModal(true)}
            />
            {showModal && (
              <CustomMetricModal
                dashboard={dashboard}
                onHide={() => setShowModal(false)}
                refetch={refetch}
              />
            )}
          </>
        )}
        <ActionsBox
          actionsData={actionsEffectiveness}
          totalActionsCount={totalActionsCount}
        />
        <UserInsightsBox
          surveys={surveys}
          userInsights={userInsights}
          dashboardId={dashboardId}
        />
        <FeedbackBox
          commentsCount={commentsCount}
          ratedCurrentEnrolments={ratedCurrentEnrolments}
        />
      </div>
    </div>
  )
}

const defaultDateRange = predefinedDateRanges[1]

export const defaultFilterValues: DateFilterType = {
  from: dayjs()
    .subtract(defaultDateRange.amount, defaultDateRange.unit)
    .startOf('day')
    .toDate(),
  to: dayjs().endOf('day').toDate(),
  period: defaultDateRange
}

export const DashboardSummary = (props) => {
  return (
    <WithDateFilterContext defaultValues={defaultFilterValues}>
      <DashboardSummaryComponent {...props} />
    </WithDateFilterContext>
  )
}
